/**
 * Custom hooks for Letter of Credit management
 */

import { useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { getContractAddress } from '@contracts/addresses'
import { LetterOfCreditABI } from '@contracts/abis'
import { Address, LetterOfCredit, LOCStatus } from '../types/contracts'
import { SUPPORTED_TOKENS } from '@contracts/tokens'
import { toast } from 'react-hot-toast'

// Types for contract read functions
export interface CollateralFactor {
  creationCollateralFactorBasisPoints: number
  collateralFactorBasisPoints: number
  liquidatorIncentiveBasisPoints: number
}

export interface CreditedTokenConfig {
  minPerDynamicLOC: bigint
  maxPerDynamicLOC: bigint
  globalMaxInDynamicUse: bigint
  globalAmountInDynamicUse: bigint
}

/**
 * Hook to fetch LOC details
 */
export function useLOCDetails(locId?: bigint) {
  const { data: locData, isLoading, refetch } = useReadContract({
    address: getContractAddress('LetterOfCreditProxy'),
    abi: LetterOfCreditABI,
    functionName: 'getLOC',
    args: locId !== undefined ? [locId] : undefined,
    query: {
      enabled: locId !== undefined,
    },
  })

  if (!locData) {
    return {
      loc: undefined,
      isLoading,
      refetch,
    }
  }

  // New getLOC return structure from actual contract ABI
  const [
    collateralId,
    creator,
    beneficiary,
    expirationTimestamp,
    _collateralFactorBasisPoints,
    _liquidatorIncentiveBasisPoints,
    _collateralContract,
    _collateralTokenAddress,
    collateralTokenAmount,
    claimableCollateral,
    creditedTokenAddress,
    creditedTokenAmount
  ] = locData as unknown as [
    bigint, // collateralId
    Address, // creator
    Address, // beneficiary
    number, // expirationTimestamp (uint32)
    number, // collateralFactorBasisPoints (uint16)
    number, // liquidatorIncentiveBasisPoints (uint16)
    Address, // collateralContract
    Address, // collateralTokenAddress
    bigint, // collateralTokenAmount
    bigint, // claimableCollateral
    Address, // creditedTokenAddress
    bigint // creditedTokenAmount
  ]

  // Determine LOC status from on-chain state (#7)
  // - If creditedTokenAmount is 0 and collateral is 0, it's been redeemed or cancelled
  // - If expirationTimestamp is 0 or all amounts are 0, LOC was cancelled
  // - If collateral is 0 but credited > 0, it was redeemed (collateral released)
  const now = Math.floor(Date.now() / 1000)
  const isEmpty = collateralTokenAmount === 0n && creditedTokenAmount === 0n
  const isExpired = expirationTimestamp > 0 && expirationTimestamp <= now
  
  let status: LOCStatus
  if (isEmpty && expirationTimestamp === 0) {
    status = LOCStatus.Cancelled
  } else if (isEmpty || (collateralTokenAmount === 0n && claimableCollateral === 0n)) {
    status = LOCStatus.Redeemed
  } else if (isExpired) {
    status = LOCStatus.Expired
  } else {
    status = LOCStatus.Active
  }
  
  const isActive = status === LOCStatus.Active

  // Find credited token symbol from supported tokens
  const creditedToken = Object.values(SUPPORTED_TOKENS).find(
    (t) => t.address.toLowerCase() === creditedTokenAddress.toLowerCase()
  )

  const loc: LetterOfCredit = {
    id: locId!,
    vaultId: collateralId, // Using collateralId as vaultId for compatibility
    creator,
    beneficiary,
    creditedAmount: creditedTokenAmount,
    creditedAsset: creditedTokenAddress,
    creditedAssetSymbol: creditedToken?.symbol || 'USDC',
    expiry: BigInt(expirationTimestamp),
    status,
    createdAt: 0, // TODO: Fetch from events
    isActive,
  }

  return {
    loc,
    isLoading,
    refetch,
  }
}

/**
 * Hook to get collateral factor for a token pair
 * Returns creation factor, collateral factor, and liquidator incentive
 */
export function useCollateralFactor(
  collateralTokenAddress?: Address,
  creditedTokenAddress?: Address
) {
  const { data, isLoading, error, refetch } = useReadContract({
    address: getContractAddress('LetterOfCreditProxy'),
    abi: LetterOfCreditABI,
    functionName: 'getCollateralFactor',
    args:
      collateralTokenAddress && creditedTokenAddress
        ? [collateralTokenAddress, creditedTokenAddress]
        : undefined,
    query: {
      enabled: !!collateralTokenAddress && !!creditedTokenAddress,
    },
  })

  // wagmi v2 with `as const` ABI returns named struct fields directly
  const collateralFactor = data
    ? {
        creationCollateralFactorBasisPoints: Number((data as any).creationCollateralFactorBasisPoints ?? 0),
        collateralFactorBasisPoints: Number((data as any).collateralFactorBasisPoints ?? 0),
        liquidatorIncentiveBasisPoints: Number((data as any).liquidatorIncentiveBasisPoints ?? 0),
      }
    : undefined

  return {
    collateralFactor,
    isLoading,
    error,
    refetch,
  }
}

/**
 * Hook to get credited token configuration (min/max per LOC, global limits)
 */
export function useCreditedTokenConfig(tokenAddress?: Address) {
  const { data, isLoading, error, refetch } = useReadContract({
    address: getContractAddress('LetterOfCreditProxy'),
    abi: LetterOfCreditABI,
    functionName: 'getCreditedToken',
    args: tokenAddress ? [tokenAddress] : undefined,
    query: {
      enabled: !!tokenAddress,
    },
  })

  // wagmi v2 with `as const` ABI returns named struct fields directly as an object
  const parsedConfig: CreditedTokenConfig | undefined = data
    ? {
        minPerDynamicLOC: (data as any).minPerDynamicLOC ?? 0n,
        maxPerDynamicLOC: (data as any).maxPerDynamicLOC ?? 0n,
        globalMaxInDynamicUse: (data as any).globalMaxInDynamicUse ?? 0n,
        globalAmountInDynamicUse: (data as any).globalAmountInDynamicUse ?? 0n,
      }
    : undefined

  return {
    config: parsedConfig,
    isLoading,
    error,
    refetch,
  }
}

/**
 * Hook to get max LOC duration in seconds
 */
export function useMaxLocDuration() {
  const { data, isLoading } = useReadContract({
    address: getContractAddress('LetterOfCreditProxy'),
    abi: LetterOfCreditABI,
    functionName: 'maxLocDurationSeconds',
  })

  return {
    maxDurationSeconds: data as number | undefined,
    isLoading,
  }
}

/**
 * Hook to issue a new LOC using createDynamicLOC
 */
export function useIssueLOC() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const issueLOC = async (
    beneficiary: Address,
    collateralTokenAddress: Address,
    collateralTokenAmount: bigint,
    creditedTokenAddress: Address,
    creditedTokenAmount: bigint,
    expirationTimestamp: number
  ) => {
    try {
      console.log('🟢 [useIssueLOC] Issuing LOC with createDynamicLOC', {
        beneficiary,
        collateralTokenAddress,
        collateralTokenAmount: collateralTokenAmount.toString(),
        creditedTokenAddress,
        creditedTokenAmount: creditedTokenAmount.toString(),
        expirationTimestamp
      })
      
      writeContract({
        address: getContractAddress('LetterOfCreditProxy'),
        abi: LetterOfCreditABI,
        functionName: 'createDynamicLOC',
        args: [
          beneficiary,
          collateralTokenAddress,
          collateralTokenAmount,
          creditedTokenAddress,
          creditedTokenAmount,
          expirationTimestamp,
          '0x', // _oraclePriceUpdate - empty bytes (use existing oracle price)
          '0x', // _collateralizableAllowanceSignature - empty bytes (no signature)
          '0x0000000000000000000000000000000000000000000000000000000000000000' // _tag - zero bytes32 (no tag)
        ],
        gas: 500000n, // Explicit gas limit for LOC issuance (complex operation)
      })
    } catch (err) {
      toast.error('Failed to issue LOC. Ensure sufficient collateral is deposited.')
      console.error('🔴 [useIssueLOC] Error:', err)
    }
  }

  return {
    issueLOC,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

/**
 * Hook to redeem an LOC
 * Uses the actual contract's redeemLOC function signature
 */
export function useRedeemLOC() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const redeemLOC = async (
    locId: bigint,
    destinationAddress: Address,
    creditedAmountToRedeem?: bigint // Optional - if not provided, redeems full amount
  ) => {
    try {
      console.log('🟢 [useRedeemLOC] Redeeming LOC', {
        locId: locId.toString(),
        destinationAddress,
        creditedAmountToRedeem: creditedAmountToRedeem?.toString() || 'full amount'
      })
      
      writeContract({
        address: getContractAddress('LetterOfCreditProxy'),
        abi: LetterOfCreditABI,
        functionName: 'redeemLOC',
        args: [
          locId,
          creditedAmountToRedeem || 0n, // 0 means full redemption
          destinationAddress,
          '0x0000000000000000000000000000000000000000', // No liquidator
          '0x', // No oracle price update
          '0x', // No beneficiary authorization
          '0x' // No liquidator params
        ],
        gas: 300000n, // Explicit gas limit for LOC redemption
      })
    } catch (err) {
      toast.error('Failed to redeem LOC. Ensure LOC is active and you are the beneficiary.')
      console.error('🔴 [useRedeemLOC] Error:', err)
    }
  }

  return {
    redeemLOC,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

/**
 * Hook to extend an LOC
 */
export function useExtendLOC() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const extendLOC = async (locId: bigint, newExpirationTimestamp: number) => {
    try {
      console.log('🟢 [useExtendLOC] Extending LOC', {
        locId: locId.toString(),
        newExpirationTimestamp
      })
      
      writeContract({
        address: getContractAddress('LetterOfCreditProxy'),
        abi: LetterOfCreditABI,
        functionName: 'extendLOC',
        args: [locId, newExpirationTimestamp],
        gas: 100000n, // Explicit gas limit for LOC extension
      })
    } catch (err) {
      toast.error('Failed to extend LOC. Ensure you are the creator and LOC is active.')
      console.error('🔴 [useExtendLOC] Error:', err)
    }
  }

  return {
    extendLOC,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

/**
 * Hook to cancel an LOC
 */
export function useCancelLOC() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const cancelLOC = async (locId: bigint) => {
    try {
      console.log('🟢 [useCancelLOC] Canceling LOC', {
        locId: locId.toString()
      })
      
      writeContract({
        address: getContractAddress('LetterOfCreditProxy'),
        abi: LetterOfCreditABI,
        functionName: 'cancelLOC',
        args: [
          locId,
          '0x' // _beneficiaryAuthorization - empty bytes (no signature)
        ],
        gas: 150000n, // Explicit gas limit for LOC cancellation
      })
    } catch (err) {
      toast.error('Failed to cancel LOC. Ensure you are the creator and LOC is active.')
      console.error('🔴 [useCancelLOC] Error:', err)
    }
  }

  return {
    cancelLOC,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

/**
 * Hook to modify LOC collateral (add or remove)
 * Uses modifyLOCCollateral from the actual contract
 */
export function useModifyLOCCollateral() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const modifyCollateral = async (locId: bigint, byAmount: bigint) => {
    try {
      console.log('🟢 [useModifyLOCCollateral] Modifying LOC collateral', {
        locId: locId.toString(),
        byAmount: byAmount.toString()
      })
      
      writeContract({
        address: getContractAddress('LetterOfCreditProxy'),
        abi: LetterOfCreditABI,
        functionName: 'modifyLOCCollateral',
        args: [
          locId,
          byAmount, // Positive to add, negative to remove
          '0x', // _oraclePriceUpdate - empty bytes
          '0x' // _collateralizableAllowanceSignature - empty bytes
        ],
        gas: 200000n, // Explicit gas limit for modifying collateral
      })
    } catch (err) {
      toast.error('Failed to modify collateral. Ensure token is approved and LOC is active.')
      console.error('🔴 [useModifyLOCCollateral] Error:', err)
    }
  }

  return {
    modifyCollateral,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

